// paymentDue.js — 발주 계획 앱(project-procurement-plan)의 '지급기일 관제' 산정 로직을 재고 앱으로 옮긴 공용 모듈.
//
// 발주 앱 public/index.html 의 계산부(parsePayTerm / dueRows / dueWeeks / dueMonthsAgg)를 그대로 옮겨 왔다.
// 두 앱이 같은 purchasePlans 문서를 읽으므로 숫자가 어긋나면 안 된다 — 로직을 고칠 때는 반드시 양쪽을 함께 고칠 것.
//
// 데이터 원천: purchasePlans (읽기 전용). procurementPlans.js 의 fetchProcurementPlans() 로 가져온 배열을 넘긴다.

// ── 기본 유틸 ────────────────────────────────────────────────
export const num = (v) => {
  if (v == null) return 0;
  const n = parseFloat(String(v).replace(/[,\s₩$]/g, ""));
  return isFinite(n) ? n : 0;
};
export const isYMD = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || "").trim());
export const won = (n) => Math.round(n || 0).toLocaleString("ko-KR");

const pad2 = (n) => ("0" + n).slice(-2);
const ymdOf = (d) => d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate());

export function addDaysYMD(ymd, days) {
  if (!ymd) return "";
  const p = String(ymd).split("-");
  if (p.length < 3) return ymd;
  const d = new Date(+p[0], +p[1] - 1, +p[2]);
  d.setDate(d.getDate() + (days || 0));
  return ymdOf(d);
}
export function eomMonth(ymd, addM) {
  if (!ymd) return "";
  const p = String(ymd).split("-");
  if (p.length < 2) return ymd;
  const d = new Date(+p[0], +p[1] - 1 + (addM || 0) + 1, 0);
  return ymdOf(d);
}
export const dueToday = () => ymdOf(new Date());
const dateObj = (ymd) => {
  const p = String(ymd || "").split("-");
  return new Date(+p[0], +p[1] - 1, +p[2]);
};
export const dueDiffDays = (a, b) => Math.round((dateObj(a) - dateObj(b)) / 86400000);
export const dueMD = (ymd) => {
  const p = String(ymd).split("-");
  return +p[1] + "/" + +p[2];
};

// 한국 공휴일 — 연 1회 갱신 필요 (발주 앱 DUE_HOLIDAYS 와 동일하게 유지할 것)
export const DUE_HOLIDAYS = {
  "2026-01-01": 1, "2026-02-16": 1, "2026-02-17": 1, "2026-02-18": 1, "2026-03-01": 1, "2026-03-02": 1,
  "2026-05-01": 1, "2026-05-05": 1, "2026-05-24": 1, "2026-05-25": 1, "2026-06-06": 1, "2026-08-15": 1,
  "2026-09-24": 1, "2026-09-25": 1, "2026-09-26": 1, "2026-09-28": 1, "2026-10-03": 1, "2026-10-05": 1,
  "2026-10-09": 1, "2026-12-25": 1,
  "2027-01-01": 1, "2027-02-05": 1, "2027-02-06": 1, "2027-02-07": 1, "2027-02-08": 1, "2027-03-01": 1,
  "2027-05-05": 1, "2027-05-13": 1, "2027-06-06": 1, "2027-06-07": 1, "2027-08-15": 1, "2027-08-16": 1,
  "2027-09-14": 1, "2027-09-15": 1, "2027-09-16": 1, "2027-10-03": 1, "2027-10-04": 1, "2027-10-09": 1, "2027-12-25": 1,
};
// 주말·공휴일이면 익영업일로 이연
export function dueNextBiz(ymd) {
  if (!isYMD(ymd)) return ymd;
  const d = dateObj(ymd);
  for (let guard = 0; guard < 20; guard++) {
    const k = ymdOf(d);
    if (d.getDay() !== 0 && d.getDay() !== 6 && !DUE_HOLIDAYS[k]) return k;
    d.setDate(d.getDate() + 1);
  }
  return ymd;
}

// ── 결제조건 파서 (발주 앱 parsePayTerm 과 동일) ──────────────
// adv:true = 선지급(발주일 고정). 그 외 회차는 B/L 일자가 있으면 B/L 기준(expandPlanPayments 참고).
export function parsePayTerm(term) {
  const t = String(term || "").trim();
  const lo = t.toLowerCase();
  if (!t) return [{ ratio: 1, base: "delivery", offset: 0 }];
  const m = t.match(/선지급\s*(\d+)\s*%[^\d]*선적\s*후\s*(\d+)\s*%/);
  if (m) return [{ ratio: +m[1] / 100, base: "order", offset: 0, adv: true }, { ratio: +m[2] / 100, base: "delivery", offset: 0 }];
  const m2 = t.match(/선적\s*(?:일\s*)?(?:후|이후|기준|로부터)\s*(\d+)\s*일/) || t.match(/B\s*\/?\s*L\s*(?:일자?)?\s*(?:후|이후|기준|로부터)?\s*(\d+)\s*일/i)
    || lo.match(/(\d+)\s*days?\s*(?:after|from)\s*(?:the\s*)?(?:shipment|b\s*\/?\s*l|bill\s*of\s*lading)/);
  if (m2) return [{ ratio: 1, base: "delivery", offset: +m2[1] }];
  if (/선적\s*후|after\s*(?:shipment|b\s*\/?\s*l)/.test(lo)) return [{ ratio: 1, base: "delivery", offset: 0 }];
  const m3 = t.match(/(\d+)\s*일\s*외상/);
  if (m3) return [{ ratio: 1, base: "order", offset: +m3[1] }];
  if (/익월말/.test(t)) return [{ ratio: 1, base: "order", offset: 0, eom: 1 }];
  if (/월말/.test(t)) return [{ ratio: 1, base: "order", offset: 0, eom: 0 }];
  if (/현금|선결제|선지급|선불/.test(t)) return [{ ratio: 1, base: "order", offset: 0, adv: true }];
  return [{ ratio: 1, base: "delivery", offset: 0 }];
}

const baseLabel = (ins, useShip) =>
  ins.base === "order" ? "발주일" : useShip ? "선적일(B/L)" : "납기(입고 예정일)";
const insLabel = (ins, n) =>
  n < 2 ? "일시불" : (ins.base === "order" ? "선지급 " : "잔금 ") + Math.round(ins.ratio * 100) + "%";

export const DUE_ST = {
  OVERDUE: ["연체", "urgent"],
  DUE: ["금일 지급", "warn"],
  PLANNED: ["지급 예정", "info"],
  PAID: ["지급 완료", "ok"],
};

// ── 기준환율 ─────────────────────────────────────────────────
// 환율은 재고 앱 첫 화면 상단 '오늘의 환율'(open.er-api.com, 1일 1회 갱신)의 USD/KRW 를 그대로 쓴다.
// dueRows(plans, { fx, fxMode }) 로 주입받으며, 별도 수기 입력칸은 두지 않는다.
// 적용 방식(fxMode)만 사용자가 고를 수 있어 localStorage 에 기억한다.
const FXMODE_KEY = "invDueFxMode";
export function getFxMode() {
  try { return localStorage.getItem(FXMODE_KEY) === "all" ? "all" : "plan"; } catch { return "plan"; }
}
export function setFxMode(m) { try { localStorage.setItem(FXMODE_KEY, m === "all" ? "all" : "plan"); } catch {} }

// ── 분할선적(인보이스) ───────────────────────────────────────
// p.shipments = [{id, blNo, invNo, blDate, etaDate, amount}] — amount 는 인보이스 금액(전표 통화 기준).
// 발주 앱 public/index.html 의 planShipments / expandPlanPayments 와 동일 규칙:
//  · 선지급(발주일 기준) 회차는 전표 전체로 1건.
//  · '선적 후' 성격 회차는 인보이스별로 쪼개 각 B/L 일자 기준으로 산정.
//  · 금액 미입력 인보이스는 (전표금액 − 입력 합계)를 균등 배분. 선적이 1건뿐이면 전액(추정 아님).
//  · 금액이 모두 입력됐는데 전표금액이 남으면 '미선적 잔량' 1건을 납기 기준으로 추가.
//  · shipments 가 없으면 예전처럼 p.blDate(없으면 납기) 기준 전표 전체 1건.
export function planShipments(p) {
  return (p && Array.isArray(p.shipments) ? p.shipments : []).filter((x) =>
    x && (num(x.amount) > 0 || String(x.blNo || "").trim() || String(x.invNo || "").trim() || isYMD(x.blDate) || isYMD(x.etaDate)));
}
export function expandPlanPayments(p) {
  const out = [];
  const amt = num(p && p.totAmt);
  if (!amt) return out;
  const order = p.issueDate || p.date || "";
  const deliv = p.date || p.issueDate || "";
  const legacyShip = isYMD(p.blDate) ? p.blDate : "";
  const sh = planShipments(p);
  // 수입(B/L 입력) 전표: 선지급(adv) 외 모든 회차 기준일 = B/L (외상·월말 포함). B/L 없는 국내 건은 기존 규칙.
  const hasBL = !!legacyShip || sh.some((x) => isYMD(x.blDate));
  const ins = parsePayTerm(p.payTerm).map((t) =>
    hasBL && t.base === "order" && !t.adv ? { ...t, base: "delivery", blCredit: true } : t);
  let known = 0, blank = 0;
  sh.forEach((x) => { const a = num(x.amount); if (a > 0) known += a; else blank++; });
  const leftover = Math.max(0, amt - known);
  const alloc = sh.map((x) => {
    const a = num(x.amount);
    return a > 0 ? { s: x, a, est: false } : { s: x, a: blank ? leftover / blank : 0, est: sh.length > 1 };
  });
  const rest = blank ? 0 : Math.max(0, amt - known);
  ins.forEach((t, i) => {
    if (t.base === "order" || !sh.length) {
      out.push({
        i, sub: 0, t, nIns: ins.length,
        base: t.base === "order" ? order : (legacyShip || deliv),
        useShip: t.base !== "order" && !!legacyShip,
        part: amt * t.ratio, pkey: String(i), legacyKey: null,
        blNo: t.base !== "order" ? String(p.blNo || "") : "", invNo: t.base !== "order" ? String(p.invNo || "") : "",
        etaDate: isYMD(p.etaDate) ? p.etaDate : "", shipNo: 0, shipCnt: 0, estAmt: false, rest: false,
      });
      return;
    }
    alloc.forEach((x, k) => {
      if (!(x.a > 0)) return;
      const us = isYMD(x.s.blDate);
      out.push({
        i, sub: k + 1, t, nIns: ins.length,
        base: us ? x.s.blDate : deliv, useShip: us,
        part: x.a * t.ratio, pkey: i + ":" + x.s.id, legacyKey: String(i),
        blNo: String(x.s.blNo || ""), invNo: String(x.s.invNo || ""), etaDate: isYMD(x.s.etaDate) ? x.s.etaDate : "",
        shipNo: k + 1, shipCnt: sh.length, estAmt: x.est, rest: false,
      });
    });
    if (rest > 0.005) {
      out.push({
        i, sub: 999, t, nIns: ins.length, base: deliv, useShip: false, part: rest * t.ratio,
        pkey: i + ":rest", legacyKey: String(i), blNo: "", invNo: "", etaDate: "",
        shipNo: 0, shipCnt: sh.length, estAmt: false, rest: true,
      });
    }
  });
  return out;
}

// ── 지급 건 전개 ─────────────────────────────────────────────
// 한 발주(전표)의 결제조건을 회차별(분할선적이면 인보이스별)로 펼쳐 '지급 예정 1건'씩 만든다.
export function dueRows(plans, opts = {}) {
  const today = dueToday();
  const fx = num(opts.fx) > 0 ? num(opts.fx) : 0;          // 재고 앱 '오늘의 환율' USD/KRW
  const fxMode = opts.fxMode || getFxMode();
  const out = [];
  (plans || []).forEach((p) => {
    if (!num(p.totAmt)) return;
    const isF = p.cur === "$" || String(p.cur || "").toUpperCase() === "USD";
    const planRate = num(p.rate) || 0;
    let rate = planRate, rateSrc = "";
    if (isF) {
      if (fxMode === "all" && fx > 0) { rate = fx; rateSrc = "fx"; }
      else if (planRate > 0) { rate = planRate; rateSrc = "plan"; }
      else if (fx > 0) { rate = fx; rateSrc = "fx"; }
      else { rate = 0; rateSrc = "none"; }
    }
    const cur = isF ? (p.curName || "USD") : "KRW";
    const paid = p.paidMap || {};
    expandPlanPayments(p).forEach((e) => {
      const t = e.t, useShip = e.useShip, base = e.base;
      if (!isYMD(base)) return;
      const shipMissing = t.base !== "order" && isF && !useShip;
      const raw = t.eom != null ? eomMonth(base, t.eom) : addDaysYMD(base, t.offset || 0);
      const due = dueNextBiz(raw);
      const part = e.part;
      const krw = isF ? (rate > 0 ? part * rate : null) : part;
      const paidAt = paid[e.pkey] || (e.legacyKey ? paid[e.legacyKey] : "") || "";
      const dd = dueDiffDays(due, today);
      const status = paidAt ? "PAID" : dd < 0 ? "OVERDUE" : dd === 0 ? "DUE" : "PLANNED";
      const sfx = e.rest ? " · 미선적" : e.shipCnt > 1 ? " · 선적" + e.shipNo : "";
      out.push({
        planId: p.id,
        slipNo: p.slipNo || "",
        vendor: p.vendor || "(업체 미지정)",
        seq: e.i,
        sub: e.sub,
        pkey: e.pkey,
        blNo: e.blNo,
        invNo: e.invNo,
        estAmt: e.estAmt,
        isRest: e.rest,
        label: insLabel(t, e.nIns) + sfx,
        term: p.payTerm || "(미지정)",
        baseKind: baseLabel(t, useShip),
        baseDate: base,
        shipMissing,
        etaDate: e.etaDate,
        rawDue: raw, due, adjusted: raw !== due,
        cur, amount: part, krw,
        noRate: isF && rate <= 0,
        rate: isF ? rate : 0,
        rateSrc,
        confirmed: dueDiffDays(base, today) <= 0,
        status, paidAt, dday: dd,
      });
    });
  });
  out.sort((a, b) => (a.due < b.due ? -1 : a.due > b.due ? 1 : 0));
  return out;
}

export const dueOpen = (rs) => rs.filter((r) => r.status !== "PAID");
export const dueSumK = (rs) => rs.reduce((t, r) => t + (r.krw != null ? r.krw : 0), 0);

const mondayOf = (ymd) => {
  const d = dateObj(ymd);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return ymdOf(d);
};

function aggregate(bucket) {
  bucket.total = dueSumK(bucket.rows);
  bucket.byCur = {};
  bucket.rows.forEach((r) => { if (r.krw != null) bucket.byCur[r.cur] = (bucket.byCur[r.cur] || 0) + r.krw; });
  bucket.est = dueSumK(bucket.rows.filter((r) => !r.confirmed));
  bucket.items = bucket.rows.slice().sort((a, b) => {
    if (a.due !== b.due) return a.due < b.due ? -1 : 1;
    if (a.vendor !== b.vendor) return a.vendor.localeCompare(b.vendor, "ko");
    return (a.seq - b.seq) || ((a.sub || 0) - (b.sub || 0));
  });
  return bucket;
}

// 12주 — 첫 칸은 기일이 지난 '연체 이월'
export function dueWeeks(rs, n = 12) {
  const today = dueToday(), mon = mondayOf(today), open = dueOpen(rs);
  const ws = [aggregate({ label: "연체", start: "", end: addDaysYMD(mon, -1), lead: true, rows: open.filter((r) => r.due < mon) })];
  for (let i = 0; i < n; i++) {
    const a = addDaysYMD(mon, i * 7), b = addDaysYMD(a, 6);
    ws.push(aggregate({ label: "W" + (i + 1), start: a, end: b, rows: open.filter((r) => r.due >= a && r.due <= b) }));
  }
  return ws;
}

export const monthAdd = (ym, k) => {
  let y = +ym.slice(0, 4), m = +ym.slice(5, 7) - 1 + k;
  y += Math.floor(m / 12); m = ((m % 12) + 12) % 12;
  return y + "-" + pad2(m + 1);
};

// 6개월 — 첫 칸은 지난달까지 남은 '이월'
export function dueMonths(rs, n = 6) {
  const today = dueToday(), cm = today.slice(0, 7), open = dueOpen(rs);
  const out = [aggregate({ label: "이월", key: "", lead: true, rows: open.filter((r) => r.due.slice(0, 7) < cm) })];
  for (let i = 0; i < n; i++) {
    const k = monthAdd(cm, i);
    out.push(aggregate({ label: +k.slice(0, 4) + "년 " + +k.slice(5, 7) + "월", key: k, rows: open.filter((r) => r.due.slice(0, 7) === k) }));
  }
  return out;
}

// 대시보드 KPI
export function dueKpis(rs) {
  const today = dueToday(), open = dueOpen(rs);
  const ov = open.filter((r) => r.status === "OVERDUE");
  const d7 = open.filter((r) => r.dday >= 0 && r.dday <= 7);
  const d30 = open.filter((r) => r.dday >= 0 && r.dday <= 30);
  const est = open.filter((r) => !r.confirmed);
  const totK = dueSumK(open);
  let maxOv = 0;
  ov.forEach((r) => { if (-r.dday > maxOv) maxOv = -r.dday; });
  return {
    today, open, totK,
    ov, ovK: dueSumK(ov), maxOv,
    d7, d7K: dueSumK(d7),
    d30, d30K: dueSumK(d30),
    est, estK: dueSumK(est),
    estPct: totK > 0 ? Math.round((dueSumK(est) / totK) * 100) : 0,
    noRate: open.filter((r) => r.noRate).length,
    noShip: open.filter((r) => r.shipMissing).length,
  };
}

export function groupBy(rows, keyFn) {
  const g = {};
  rows.forEach((r) => { if (r.krw != null) { const k = keyFn(r); g[k] = (g[k] || 0) + r.krw; } });
  return Object.keys(g).map((k) => ({ n: k, v: g[k] })).sort((a, b) => b.v - a.v);
}
